import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Address, decodeFunctionResult, encodeFunctionData, getAbiItem, Hex, InferItemName } from 'viem'
import { GetAbiItemReturnType } from 'viem/dist/types/utils/abi/getAbiItem'

import { useContract } from '../../hooks/useContract'
import { useBlockNumber, useSessionChainId } from '../application/hooks'
import { addMulticallListeners, ListenerOptions, removeMulticallListeners } from './actions'
import { WithMulticallState } from './reducer'
import { Call, parseCallKey, toCallKey } from './utils'

// Result is an array of any elements with string indices
export interface Result extends ReadonlyArray<any> {
  readonly [key: string]: any
}

// MethodArg is a string, number, or bigint
type MethodArg = string | number | bigint

// MethodArgs is an array of MethodArg or MethodArg[]
type MethodArgs = Array<MethodArg | MethodArg[]>

// OptionalMethodInputs is an array of MethodArg, MethodArg[], or undefined
export type OptionalMethodInputs = Array<MethodArg | MethodArg[] | undefined> | undefined

/**
 * Returns true if x is a MethodArg, false otherwise
 * @param {unknown} x
 * @return {MethodArg}
 */
function isMethodArg(x: unknown): x is MethodArg {
  return typeof x === 'bigint' || ['string', 'number'].indexOf(typeof x) !== -1
}

/**
 * Returns true if x is a valid MethodArgs or undefined, false otherwise
 * @param {unknown} x
 * @return {undefined}
 */
function isValidMethodArgs(x: unknown): x is MethodArgs | undefined {
  return (
    x === undefined ||
    (Array.isArray(x) &&
      x.every((xi) => isMethodArg(xi) || (Array.isArray(xi) && xi.every(isMethodArg))))
  )
}

// CallResult is an object with a boolean 'valid' field, a 'data' field of type string or undefined, and a 'blockNumber' field of type number or undefined
interface CallResult {
  readonly valid: boolean
  readonly data: Hex | undefined
  readonly blockNumber: number | undefined
}

// INVALID_RESULT is a constant CallResult with valid field set to false and data and blockNumber fields set to undefined
const INVALID_RESULT: CallResult = { valid: false, blockNumber: undefined, data: undefined }

// NEVER_RELOAD is a constant object with blocksPerFetch field set to Infinity
export const NEVER_RELOAD: ListenerOptions = {
  blocksPerFetch: Infinity,
}

/**
 * useCallsData is a hook that subscribes to contract data given a list of calls and an options object. It returns an array of CallResults.
 * @param {(Call | undefined)[]} calls - an array of Calls or undefined
 * @param {ListenerOptions} blocksPerFetch [{ blocksPerFetch }={ blocksPerFetch: 1 }] - an options object with a blocksPerFetch field
 * @return {CallResult[]}
 */
function useCallsData(
  calls: (Call | undefined)[],
  { blocksPerFetch }: ListenerOptions = { blocksPerFetch: 1 }
): CallResult[] {
  // get the current chainId from the session
  const [chainId] = useSessionChainId()
  // get the callResults object from the global state
  const callResults = useSelector((state: WithMulticallState) => state.multicall.callResults)
  // get the dispatch function from the global state
  const dispatch = useDispatch()

  // serialize the callKeys in the calls array to a string
  const serializedCallKeys: string = useMemo(
    () =>
      JSON.stringify(
        calls
          ?.filter((c): c is Call => Boolean(c))
          ?.map(toCallKey)
          ?.sort() ?? []
      ),
    [calls]
  )

  // update listeners when there is an actual change that persists for at least 100ms
  useEffect(() => {
    // parse the serializedCallKeys back into an array of callKeys
    const callKeys: Hex[] = JSON.parse(serializedCallKeys)
    // if the chainId is not defined or there are no callKeys, return undefined
    if (!chainId || callKeys.length === 0) return undefined
    // convert the callKeys back into calls
    const calls = callKeys.map((key) => parseCallKey(key))
    // add multicall listeners for the chainId, calls, and options
    dispatch(
      addMulticallListeners({
        chainId: chainId,
        calls,
        options: { blocksPerFetch },
      })
    )

    // remove multicall listeners for the chainId, calls, and options when the effect unmounts
    return () => {
      dispatch(removeMulticallListeners({ chainId: chainId, calls, options: { blocksPerFetch } }))
    }
  }, [chainId, dispatch, blocksPerFetch, serializedCallKeys])

  // return an array of CallResults based on the calls array
  return useMemo(
    () =>
      calls.map<CallResult>((call) => {
        // if the chainId or call is not defined, return the INVALID_RESULT constant
        if (!chainId || !call) return INVALID_RESULT

        // get the result for the current call from the callResults object
        const result = callResults[chainId]?.[toCallKey(call)]
        let data
        // if the result has a defined data field that is not '0x', set the data variable to the data field
        if (result?.data && result?.data !== '0x') {
          data = result.data
        }

        // return a CallResult object with valid field set to true, data set to the data variable, and blockNumber set to the result's blockNumber field
        return { valid: true, data, blockNumber: result?.blockNumber }
      }),
    [callResults, calls, chainId]
  )
}

// CallState is an object with a 'call' field of type Call, a 'result' field of type Result, and a 'error' field of type Error or undefined
export interface CallState {
  readonly valid: boolean
  // the result, or undefined if loading or errored/no data
  readonly result: Result | undefined
  // true if the result has never been fetched
  readonly loading: boolean
  // true if the result is not for the latest block
  readonly syncing: boolean
  // true if the call was made and is synced, but the return data is invalid
  readonly error: boolean
}

const INVALID_CALL_STATE: CallState = {
  // CallState object to return when call is invalid or CallResult is undefined
  valid: false,
  result: undefined,
  loading: false,
  syncing: false,
  error: false,
}
const LOADING_CALL_STATE: CallState = {
  // CallState object to return when CallResult is valid but required parameters are undefined
  valid: true,
  result: undefined,
  loading: true,
  syncing: true,
  error: false,
}
/**
 * Converts a CallResult object and other parameters into a CallState object
 *
 * @param {CallResult | undefined} callResult - Result of a smart contract call
 * @param {Interface | undefined} contractInterface - Interface for decoding the result of the call
 * @param {FunctionFragment| undefined} fragment - Fragment of the contract's ABI containing the method being called
 * @param {number | undefined} latestBlockNumber - Latest block number
 * @return {CallState} - CallState object representing the result of the call
 */
function toCallState(
  callResult: CallResult | undefined,
  contractInterface: ReturnType<typeof useContract> | undefined,
  fragment: GetAbiItemReturnType | undefined,
  latestBlockNumber: number | undefined
): CallState {
  if (!callResult) return INVALID_CALL_STATE // Return invalid if callResult is undefined
  const { valid, data, blockNumber } = callResult
  if (!valid) return INVALID_CALL_STATE // Return invalid if callResult is invalid
  if (valid && !blockNumber) return LOADING_CALL_STATE // Return loading if blockNumber is undefined
  if (!contractInterface || !fragment || !latestBlockNumber) return LOADING_CALL_STATE // Return loading if required parameters are undefined
  const success = data && data.length > 2 // Determine if call was successful based on data length
  const syncing = (blockNumber ?? 0) < latestBlockNumber // Determine if contract call is still syncing
  let result: Result | undefined = undefined // Initialize result as undefined
  if (success && data) {
    try {
      // Try to decode result using contractInterface and fragment
      result = decodeFunctionResult<any, InferItemName>({
        abi: contractInterface.abi,
        functionName: fragment.name,
        data: data,
      })
    } catch (error) {
      console.debug('Result data parsing failed', fragment, data)
      // Return CallState object with error set to true if decoding fails
      return {
        valid: true,
        loading: false,
        error: true,
        syncing,
        result,
      }
    }
  }
  // Return CallState object with success or error based on whether call was successful
  return {
    valid: true,
    loading: false,
    syncing,
    result: result,
    error: !success,
  }
}

/**
 * Makes multiple calls to a single contract using different input arguments and returns an array of CallState objects
 *
 * @param {Contract | null | undefined} contract - Contract to call
 * @param {string} methodName - Name of the method to call on the contract
 * @param {OptionalMethodInputs[]} callInputs - Array of input arguments to pass to the method
 * @param {ListenerOptions} options - Options for the listener
 * @param {number} gasRequired - Amount of gas required for the call
 * @return {CallState[]} - Array of CallState objects representing the result of each call
 */
export function useSingleContractMultipleData(
  contract: ReturnType<typeof useContract> | undefined,
  methodName: InferItemName,
  callInputs: OptionalMethodInputs[],
  options?: ListenerOptions,
  gasRequired?: number
): CallState[] {
  // Use memoization to create an array of Call objects
  const fragment = useMemo(
    () => contract?.abi && getAbiItem({ abi: contract.abi, name: methodName }),
    [contract, methodName]
  )
  const calls = useMemo(
    () =>
      contract &&
      fragment &&
      callInputs?.length > 0 &&
      callInputs.every((inputs) => isValidMethodArgs(inputs))
        ? callInputs.map<Call>((inputs) => {
            // Create a Call object for each set of input arguments
            return {
              address: contract.address,
              callData: encodeFunctionData({
                abi: contract.abi,
                functionName: fragment.name,
                args: inputs ? inputs : [],
              }),
              ...(gasRequired ? { gasRequired } : {}), // Optional gas requirement
            }
          })
        : [],
    [contract, fragment, callInputs, gasRequired]
  )

  // Use the useCallsData hook to make the calls and get the results
  const results = useCallsData(calls, options)

  // Use the useBlockNumber hook to get the latest block number
  const latestBlockNumber = useBlockNumber()

  // Use memoization to return an array of CallState objects
  return useMemo(() => {
    return results.map((result) => toCallState(result, contract, fragment, latestBlockNumber))
  }, [fragment, contract, results, latestBlockNumber])
}

/**
 * Makes a single call to multiple contracts using the same input arguments and returns an array of CallState objects
 *
 * @param {(string | undefined)[]}addresses - Array of contract addresses
 * @param {Interface}contractInterface - Interface for encoding the data for the call and decoding the result
 * @param {string}methodName - Name of the method to call on the contracts
 * @param {OptionalMethodInputs}callInputs - Input arguments to pass to the method
 * @param {ListenerOptions}options - Options for the listener
 * @param {number}gasRequired - Amount of gas required for the call
 * @return {CallState[]} - Array of CallState objects representing the result of each call
 */
export function useMultipleContractSingleData(
  addresses: (Address | undefined)[],
  contractInterface: ReturnType<typeof useContract> | undefined,
  methodName: InferItemName,
  callInputs?: OptionalMethodInputs,
  options?: ListenerOptions,
  gasRequired?: number
): CallState[] {
  // Use memoization to get the FunctionFragment for the method
  const fragment = useMemo(
    () => contractInterface?.abi && getAbiItem({ abi: contractInterface.abi, name: methodName }),
    [contractInterface, methodName]
  )
  const callData = useMemo(
    () =>
      contractInterface && fragment && isValidMethodArgs(callInputs)
        ? encodeFunctionData({
            abi: contractInterface.abi,
            functionName: fragment.name,
            args: callInputs ? callInputs : [],
          })
        : undefined,
    [callInputs, contractInterface, fragment]
  )

  // Use memoization to create an array of Call objects
  const calls = useMemo(
    () =>
      fragment && addresses && addresses.length > 0 && callData
        ? addresses.map<Call | undefined>((address) => {
            // Create a Call object for each contract address
            return address && callData
              ? {
                  address,
                  callData,
                  ...(gasRequired ? { gasRequired } : {}),
                }
              : undefined
          })
        : [],
    [addresses, callData, fragment, gasRequired]
  )

  // Use the useCallsData hook to make the calls and get the results
  const results = useCallsData(calls, options)

  // Use the useBlockNumber hook to get the latest block number
  const latestBlockNumber = useBlockNumber()

  // Use memoization to return an array of CallState objects
  return useMemo(() => {
    return results.map((result) =>
      toCallState(result, contractInterface, fragment, latestBlockNumber)
    )
  }, [fragment, contractInterface, results, latestBlockNumber])
}

/**
 * Makes a single call to a contract and returns the result as a CallState object
 *
 * @param {Contract | null | undefined} contract - Contract to call
 * @param {string} methodName - Name of the method to call on the contract
 * @param {OptionalMethodInputs} inputs - Input arguments to pass to the method
 * @param {ListenerOptions} options - Options for the listener
 * @param {number} gasRequired - Amount of gas required for the call
 * @return {CallState} - CallState object representing the result of the call
 */
export function useSingleCallResult(
  contract: ReturnType<typeof useContract> | null,
  methodName: string,
  inputs?: OptionalMethodInputs,
  options?: ListenerOptions,
  gasRequired?: number
): CallState {
  // Use memoization to get the FunctionFragment for the method
  const fragment = useMemo(
    () => contract?.abi && getAbiItem({ abi: contract.abi, name: methodName }),
    [contract, methodName]
  )

  // Use memoization to create a single Call object
  const calls = useMemo<Call[]>(() => {
    return contract?.abi && fragment && isValidMethodArgs(inputs)
      ? [
          {
            address: contract.address,
            callData: encodeFunctionData({
              abi: contract.abi,
              functionName: fragment.name,
              args: inputs ? inputs : [],
            }),
            ...(gasRequired ? { gasRequired } : {}),
          },
        ]
      : []
  }, [contract, fragment, inputs, gasRequired])

  // Use the useCallsData hook to make the call and get the result
  const result = useCallsData(calls, options)[0]

  // Use the useBlockNumber hook to get the latest block number
  const latestBlockNumber = useBlockNumber()

  // Use memoization to return a CallState object
  return useMemo(() => {
    return toCallState(result, contract, fragment, latestBlockNumber)
  }, [result, contract, fragment, latestBlockNumber])
}
